import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2, KeyRound, UserCheck, Info, Fingerprint } from "lucide-react";
import { api } from "../lib/api";
import { loginWithPasskey, passkeyPlatformAvailable } from "../lib/passkey";
import { useAuth } from "../context/AuthContext";

export const EmployeeLoginDialog = ({ open, onOpenChange }) => {
  const { setUser, checkAuth } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [bioSupported, setBioSupported] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);

  useEffect(() => { passkeyPlatformAvailable().then(setBioSupported); }, []);

  const reset = () => { setIdentifier(""); setPassword(""); setBusy(false); };

  const doPasskeyLogin = async () => {
    if (!identifier.trim()) { toast.warning("أدخل رقم الإقامة أو الرقم الوظيفي أولاً"); return; }
    setBioBusy(true);
    try {
      const u = await loginWithPasskey(identifier.trim(), "employee");
      setUser({ kind: "employee", ...u });
      toast.success(`مرحباً ${u.name || "بك"}`);
      onOpenChange(false);
      reset();
      await checkAuth();
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "تم إلغاء العملية"
        : (e?.response?.data?.detail || "تعذّر الدخول بالبصمة، سجّل بكلمة المرور وفعّلها من إعداداتك"));
    } finally { setBioBusy(false); }
  };

  useEffect(() => {
    const id = identifier.trim();
    if (!open || !/^1\d{9}$/.test(id) || password) return;
    let alive = true;
    api.portalLogin(id, "").then(async (u) => {
      if (!alive) return;
      setUser({ kind: "employee", ...u });
      toast.success(`مرحباً ${u.name || "بك"}`);
      onOpenChange(false); reset(); await checkAuth();
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, open]);

  const doLogin = async () => {
    if (!identifier.trim()) { toast.warning("أدخل رقم الإقامة أو الرقم الوظيفي"); return; }
    setBusy(true);
    try {
      const u = await api.portalLogin(identifier.trim(), password);
      setUser({ kind: "employee", ...u });
      toast.success(`مرحباً ${u.name || "بك"}`);
      onOpenChange(false);
      reset();
      await checkAuth();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تسجيل الدخول"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-sm font-body" dir="rtl" data-testid="employee-login-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" /> دخول الموظفين
          </DialogTitle>
          <DialogDescription className="text-right">
            ادخل برقم الإقامة/الهوية. إن كان أوّل دخول لك اترك خانة كلمة المرور فارغة، ثم عيّن كلمة مرور جديدة.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="emp-ident">رقم الإقامة / الرقم الوظيفي أو البريد الإلكتروني</Label>
            <Input id="emp-ident" data-testid="emp-identifier-input" dir="ltr" inputMode="email" autoCapitalize="none"
              placeholder="10xxxxxxxx أو name@mail.com" value={identifier}
              onChange={(e) => setIdentifier(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="emp-pass">كلمة المرور <span className="text-slate-400">(اتركها فارغة إذا كان أول دخول لك)</span></Label>
            <Input id="emp-pass" data-testid="emp-password-input" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} />
          </div>
          <Button data-testid="emp-login-btn" disabled={busy} onClick={doLogin}
            className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} تسجيل الدخول
          </Button>
          {bioSupported && (
            <Button data-testid="emp-passkey-btn" variant="outline" disabled={bioBusy} onClick={doPasskeyLogin}
              className="w-full border-blue-200 text-blue-700 hover:bg-blue-50 gap-1.5">
              {bioBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} الدخول بالبصمة
            </Button>
          )}
          <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-100 p-2.5 text-xs text-slate-500" data-testid="emp-forgot-note">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>أول دخول؟ أدخل رقم هويتك واترك كلمة المرور فارغة، ثم عيّن كلمة مرور جديدة. نسيت كلمة مرورك؟ تواصل مع مديرك.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
